import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  getDoc, 
  getDocs,
  query, 
  where, 
  orderBy,
  Timestamp,
  runTransaction
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Quotation, QuotationItem } from '../types/quotation';
import { Order, CreateOrderData, OrderItem } from '../types/order';
import { DeliveryNote, CreateDeliveryNoteData, DeliveryNoteItem } from '../types/deliveryNote';
import { generateUniqueNumber } from '../utils/numberGenerator';

export type DocumentType = 'quotation' | 'order' | 'delivery_note' | 'invoice';

export interface DocumentChainItem {
  id: string;
  type: DocumentType;
  number: string;
  status: string;
  createdAt: Date;
  parentDocumentId?: string;
  childDocuments?: string[];
}

export class DocumentChainService {
  // Árajánlatból megrendelés készítése
  static async createOrderFromQuotation(
    quotation: Quotation,
    organizationId: string,
    createdBy: string,
    customData?: Partial<CreateOrderData>
  ): Promise<Order> {
    try {
      // Árajánlat tételek konvertálása megrendelés tételekre
      const orderItems: OrderItem[] = quotation.items.map(item => ({
        productId: item.productId,
        productName: item.productName,
        productCode: item.productCode,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        price: item.price,
        taxRate: item.taxRate,
        discount: item.discount,
        lineTotal: item.lineTotal,
        lineTotalWithTax: item.lineTotalWithTax,
        currency: item.currency,
        deliveredQuantity: 0, // Még nincs szállítva
        notes: ''
      }));

      // Egyedi megrendelés szám generálása
      const orderNumber = await generateUniqueNumber('orders', 'MR');

      const orderData: CreateOrderData = {
        status: 'pending',
        number: orderNumber,
        organizationId,
        customerId: quotation.customerId,
        quotationId: quotation.id,
        
        // Ügyfél adatok átvétele
        customerName: quotation.customerName,
        customerAddress: quotation.customerAddress,
        customerTaxNumber: quotation.customerTaxNumber,
        customerEmail: quotation.customerEmail,
        customerPhone: quotation.customerPhone,
        contactName: quotation.contactName,
        
        // Szervezet adatok átvétele
        organizationName: quotation.organizationName,
        organizationAddress: quotation.organizationAddress,
        organizationTaxNumber: quotation.organizationTaxNumber,
        organizationRegistrationNumber: quotation.organizationRegistrationNumber,
        representativeName: quotation.representativeName,
        
        // Dátumok
        orderDate: new Date().toISOString().split('T')[0],
        requestedDeliveryDate: quotation.validUntil,
        
        // Pénzügyi adatok átvétele
        subtotal: quotation.subtotal,
        taxAmount: quotation.taxAmount,
        totalAmount: quotation.totalAmount,
        currency: quotation.currency,
        
        // Szállítási adatok
        deliveryAddress: quotation.customerAddress,
        paymentTerms: quotation.paymentDue,
        
        items: orderItems,
        notes: quotation.notes || ''
      };

      // Csak a létező shareSettings-et adjuk hozzá
      if (quotation.shareSettings) {
        orderData.shareSettings = quotation.shareSettings;
      }

      // Custom data override - csak a nem undefined értékekkel
      if (customData) {
        const cleanCustomData = Object.fromEntries(
          Object.entries(customData).filter(([_, value]) => value !== undefined)
        );
        Object.assign(orderData, cleanCustomData);
      }

      // Transaction-ben létrehozzuk a megrendelést és frissítjük a kapcsolatokat
      const order = await runTransaction(db, async (transaction) => {
        // ELŐSZÖR: Minden olvasást végrehajtunk (Firestore szabály)
        const quotationRef = doc(db, 'quotations', quotation.id);
        const quotationDoc = await transaction.get(quotationRef);
        
        // UTÁNA: Megrendelés létrehozása
        const orderRef = doc(collection(db, 'orders'));
        const newOrder: Order = {
          ...orderData,
          id: orderRef.id,
          parentDocumentId: quotation.id,
          childDocuments: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          createdBy
        };
        
        // Undefined értékek eltávolítása Firestore számára
        const cleanOrderData = Object.fromEntries(
          Object.entries({
            ...newOrder,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now()
          }).filter(([_, value]) => value !== undefined)
        );
        
        // ÍRÁSOK: Megrendelés mentése
        transaction.set(orderRef, cleanOrderData);

        // Árajánlat frissítése a child document hivatkozással
        
        if (quotationDoc.exists()) {
          const existingData = quotationDoc.data();
          const childDocuments = existingData.childDocuments || [];
          
          transaction.update(quotationRef, {
            childDocuments: [...childDocuments, orderRef.id],
            updatedAt: Timestamp.now()
          });
        }

        return newOrder;
      });

      return order;

    } catch (error) {
      console.error('Error creating order from quotation:', error);
      throw new Error('Hiba történt a megrendelés létrehozásakor');
    }
  }

  // Megrendelésből szállítólevél készítése
  static async createDeliveryNoteFromOrder(
    order: Order,
    organizationId: string,
    createdBy: string,
    customData?: Partial<CreateDeliveryNoteData>
  ): Promise<DeliveryNote> {
    try {
      // Megrendelés tételek konvertálása szállítólevél tételekre
      const deliveryItems: DeliveryNoteItem[] = order.items.map(item => ({
        productId: item.productId,
        productName: item.productName,
        productCode: item.productCode,
        orderedQuantity: item.quantity,
        deliveredQuantity: item.quantity, // Alapértelmezés szerint minden szállítva
        unitPrice: item.unitPrice,
        price: item.price,
        taxRate: item.taxRate,
        discount: item.discount,
        lineTotal: item.lineTotal,
        lineTotalWithTax: item.lineTotalWithTax,
        currency: item.currency,
        notes: item.notes || ''
      }));

      // Egyedi szállítólevél szám generálása
      const deliveryNumber = await generateUniqueNumber('delivery_notes', 'SL');

      const deliveryData: CreateDeliveryNoteData = {
        status: 'pending',
        number: deliveryNumber,
        organizationId,
        customerId: order.customerId,
        orderId: order.id,
        quotationId: order.quotationId,
        
        // Ügyfél adatok átvétele
        customerName: order.customerName,
        customerAddress: order.customerAddress,
        customerTaxNumber: order.customerTaxNumber,
        customerEmail: order.customerEmail,
        customerPhone: order.customerPhone,
        contactName: order.contactName,
        
        // Szervezet adatok átvétele
        organizationName: order.organizationName,
        organizationAddress: order.organizationAddress,
        organizationTaxNumber: order.organizationTaxNumber,
        organizationRegistrationNumber: order.organizationRegistrationNumber,
        representativeName: order.representativeName,
        
        // Dátumok
        deliveryDate: new Date().toISOString().split('T')[0],
        expectedDeliveryDate: order.confirmedDeliveryDate || order.requestedDeliveryDate,
        
        // Szállítási adatok
        deliveryAddress: order.deliveryAddress || order.customerAddress,
        deliveryInstructions: order.deliveryInstructions,
        
        // Pénzügyi adatok átvétele (opcionális)
        subtotal: order.subtotal,
        taxAmount: order.taxAmount,
        totalAmount: order.totalAmount,
        currency: order.currency,
        
        items: deliveryItems,
        notes: order.notes || ''
      };

      // Csak a létező shareSettings-et adjuk hozzá
      if (order.shareSettings) {
        deliveryData.shareSettings = order.shareSettings;
      }

      // Custom data override - csak a nem undefined értékekkel
      if (customData) {
        const cleanCustomData = Object.fromEntries(
          Object.entries(customData).filter(([_, value]) => value !== undefined)
        );
        Object.assign(deliveryData, cleanCustomData);
      }

      // Transaction-ben létrehozzuk a szállítólevelet és frissítjük a kapcsolatokat
      const deliveryNote = await runTransaction(db, async (transaction) => {
        // ELŐSZÖR: Minden olvasást végrehajtunk (Firestore szabály)
        const orderRef = doc(db, 'orders', order.id);
        const orderDoc = await transaction.get(orderRef);
        
        // UTÁNA: Szállítólevél létrehozása
        const deliveryRef = doc(collection(db, 'delivery_notes'));
        const newDeliveryNote: DeliveryNote = {
          ...deliveryData,
          id: deliveryRef.id,
          parentDocumentId: order.id,
          childDocuments: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          createdBy
        };
        
        // Undefined értékek eltávolítása Firestore számára
        const cleanDeliveryData = Object.fromEntries(
          Object.entries({
            ...newDeliveryNote,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now()
          }).filter(([_, value]) => value !== undefined)
        );
        
        // ÍRÁSOK: Szállítólevél mentése
        transaction.set(deliveryRef, cleanDeliveryData);

        // Megrendelés frissítése a child document hivatkozással
        if (orderDoc.exists()) {
          const existingData = orderDoc.data();
          const childDocuments = existingData.childDocuments || [];
          
          transaction.update(orderRef, {
            childDocuments: [...childDocuments, deliveryRef.id],
            updatedAt: Timestamp.now()
          });
        }

        return newDeliveryNote;
      });

      return deliveryNote;

    } catch (error) {
      console.error('Error creating delivery note from order:', error);
      throw new Error('Hiba történt a szállítólevél létrehozásakor');
    }
  }

  // Dokumentum lánc lekérése
  static async getDocumentChain(documentId: string, documentType: DocumentType): Promise<DocumentChainItem[]> {
    try {
      const chain: DocumentChainItem[] = [];
      
      // Kezdjük a gyökér dokumentummal
      let currentDoc = await this.getDocumentById(documentId, documentType);
      if (!currentDoc) return chain;

      // Keressük meg a gyökér dokumentumot (ami nincs parent-je)
      while (currentDoc.parentDocumentId) {
        const parentType = await this.getDocumentType(currentDoc.parentDocumentId);
        if (parentType) {
          currentDoc = await this.getDocumentById(currentDoc.parentDocumentId, parentType);
          if (!currentDoc) break;
        } else {
          break;
        }
      }

      // Építsük fel a láncot a gyökértől
      if (currentDoc) {
        await this.buildChainFromDocument(currentDoc, chain);
      }

      return chain;

    } catch (error) {
      console.error('Error getting document chain:', error);
      return [];
    }
  }

  // Privát segéd metódusok
  private static async getDocumentById(id: string, type: DocumentType): Promise<any> {
    try {
      const collectionName = this.getCollectionName(type);
      const docRef = doc(db, collectionName, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
    } catch (error) {
      console.warn(`Error getting document ${id} from ${type}:`, error);
    }
    return null;
  }

  private static async getDocumentType(documentId: string): Promise<DocumentType | null> {
    const collections: Array<{ name: string; type: DocumentType }> = [
      { name: 'quotations', type: 'quotation' },
      { name: 'orders', type: 'order' },
      { name: 'delivery_notes', type: 'delivery_note' }
    ];

    for (const col of collections) {
      try {
        const docRef = doc(db, col.name, documentId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          return col.type;
        }
      } catch (error) {
        // Ha a kollekció nem létezik, folytassuk a következővel
        console.warn(`Collection ${col.name} might not exist yet:`, error);
        continue;
      }
    }
    return null;
  }

  private static async buildChainFromDocument(document: any, chain: DocumentChainItem[]): Promise<void> {
    // Hozzáadjuk az aktuális dokumentumot a lánchoz
    chain.push({
      id: document.id,
      type: this.getDocumentTypeFromData(document),
      number: document.number,
      status: document.status,
      createdAt: document.createdAt?.toDate ? document.createdAt.toDate() : new Date(document.createdAt),
      parentDocumentId: document.parentDocumentId,
      childDocuments: document.childDocuments || []
    });

    // Rekurzívan hozzáadjuk a gyermek dokumentumokat
    if (document.childDocuments && document.childDocuments.length > 0) {
      for (const childId of document.childDocuments) {
        const childType = await this.getDocumentType(childId);
        if (childType) {
          const childDoc = await this.getDocumentById(childId, childType);
          if (childDoc) {
            await this.buildChainFromDocument(childDoc, chain);
          }
        }
      }
    }
  }

  private static getDocumentTypeFromData(document: any): DocumentType {
    // Próbálunk következtetni a dokumentum típusára a mezők alapján
    if (document.quotationNumber !== undefined || document.validUntil !== undefined) {
      return 'quotation';
    }
    if (document.orderId !== undefined || document.requestedDeliveryDate !== undefined) {
      return 'delivery_note';
    }
    if (document.quotationId !== undefined || document.orderDate !== undefined) {
      return 'order';
    }
    return 'quotation'; // default
  }

  private static getCollectionName(type: DocumentType): string {
    switch (type) {
      case 'quotation': return 'quotations';
      case 'order': return 'orders';
      case 'delivery_note': return 'delivery_notes';
      case 'invoice': return 'invoices';
      default: return 'quotations';
    }
  }

  // Dokumentum státusz frissítése a láncban
  static async updateDocumentStatus(
    documentId: string, 
    documentType: DocumentType, 
    newStatus: string
  ): Promise<void> {
    try {
      const collectionName = this.getCollectionName(documentType);
      const docRef = doc(db, collectionName, documentId);
      
      await updateDoc(docRef, {
        status: newStatus,
        updatedAt: Timestamp.now()
      });

    } catch (error) {
      console.error('Error updating document status:', error);
      throw new Error('Hiba történt a státusz frissítésekor');
    }
  }
} 